import type {
  PolicyReportContextBlock,
  SafetyControlReviewContextBlock,
} from '../policies/policyPacks';
import { buildReportContextForAlias, buildSafetyControlReviewForAlias } from '../policies/policyPacks';
import type {
  PolicyReportContextPayload,
  SafetyCaseRecord,
  SafetyDashboardRow,
  SafetyControlReviewPayload,
  SafetyReportArtifactDetailResponse,
  ReliabilityProofPackPreviewResponse,
} from '../../services/reportsApi';

export type SafetyControlReviewViewModel = {
  controlObjective: string;
  observedExposure: string;
  existingControlsToVerify: string[];
  potentialControlGaps: string[];
  fieldVerificationQuestions: string[];
  verificationStatus: string;
  verificationStatusLabel: string;
  primaryControlLens: string;
  effectivenessBoundary: string;
  closureRequirements: string[];
  uxSummary: string;
  reportSummary: string;
  claimBoundary: string;
  sourceLabel: string;
};

const VERIFICATION_STATUS_LABELS: Record<string, string> = {
  pending_field_verification: 'Field verification pending EHS / Operations review',
  field_verification_in_progress: 'Field verification in progress',
  field_verification_recorded: 'Field verification recorded',
  future_template: 'Future template',
};

export const V9_PENDING_DECISION_LABEL = 'Site decision pending';
export const V9_PENDING_RISK_RATING_LABEL = 'Risk rating pending customer assessment';
export const V9_PENDING_FIELD_VERIFICATION_LABEL = 'Field verification pending EHS / Operations review';
export const V9_PENDING_CLOSURE_LABEL = 'Not ready for closure';
export const V9_PENDING_FOLLOW_UP_LABEL = 'Follow-up observation not yet reviewed';

export type SafetyControlReviewV9ParityViewModel = {
  reportMode: string;
  sectionKeys: string[];
  reviewerDecisionStatus: string;
  privacyRedacted: boolean;
  fieldVerificationStatus: string;
  customerOwnedRiskBoundary: string;
  correctiveActionStatus: string;
  followUpClaimBoundary: string;
  closureStatus: string;
  secureEvidenceReferencePolicy: string;
};

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asText(value: unknown): string {
  return String(value || '').trim();
}

function asList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
}

function verificationStatusLabel(status: string): string {
  const key = asText(status).toLowerCase();
  return VERIFICATION_STATUS_LABELS[key] || V9_PENDING_FIELD_VERIFICATION_LABEL;
}

function normalizeReview(value: unknown): SafetyControlReviewContextBlock | SafetyControlReviewPayload | null {
  const raw = asObject(value);
  const hasContent = Object.keys(raw).some((key) => {
    const current = raw[key];
    return (Array.isArray(current) && current.length > 0) || asText(current).length > 0;
  });
  return hasContent ? (raw as SafetyControlReviewContextBlock) : null;
}

function toViewModelFromReview(
  review: SafetyControlReviewContextBlock | SafetyControlReviewPayload | unknown,
): SafetyControlReviewViewModel | null {
  const normalized = normalizeReview(review);
  if (!normalized) return null;
  const verificationStatus = asText(normalized.verification_status) || 'pending_field_verification';
  return {
    controlObjective: asText(normalized.control_objective),
    observedExposure: asText(normalized.observed_exposure),
    existingControlsToVerify: asList(normalized.existing_controls_to_verify),
    potentialControlGaps: asList(normalized.potential_control_gaps),
    fieldVerificationQuestions: asList(normalized.field_verification_questions),
    verificationStatus,
    verificationStatusLabel: verificationStatusLabel(verificationStatus),
    primaryControlLens: asText(normalized.primary_control_lens),
    effectivenessBoundary: asText(normalized.effectiveness_boundary),
    closureRequirements: asList(normalized.closure_requirements),
    uxSummary: asText(normalized.ux_summary),
    reportSummary: asText(normalized.report_summary),
    claimBoundary: asText(normalized.claim_boundary),
    sourceLabel: asText(normalized.source_label) || 'WorkSafeBC-aligned safety control review',
  };
}

function toViewModel(
  policyContext: PolicyReportContextPayload | PolicyReportContextBlock | null | undefined,
): SafetyControlReviewViewModel | null {
  const context = policyContext || null;
  if (!context) return null;
  return toViewModelFromReview(context.safety_control_review);
}

function inferAliasFromRecord(record: {
  riskType?: unknown;
  activityType?: unknown;
  riskLabel?: unknown;
  hazardCategory?: unknown;
  unsafeConditionTitle?: unknown;
}): string | null {
  const riskType = asText(record.riskType).toLowerCase();
  const text = [
    record.activityType,
    record.riskLabel,
    record.hazardCategory,
    record.unsafeConditionTitle,
  ]
    .map(asText)
    .join(' ')
    .toLowerCase();
  if (text.includes('material load')) return 'material_load_interaction';
  if (riskType === 'ppe_compliance' || text.includes('ppe')) return 'ppe_compliance';
  if (riskType === 'work_at_height' || text.includes('work at height') || text.includes('fall')) {
    return 'work_at_height';
  }
  if (text.includes('ergonomic') || text.includes('material handling') || text.includes('msi')) {
    return 'ergonomics_material_handling';
  }
  if (riskType === 'zone_intrusion' && (text.includes('forklift') || text.includes('mobile equipment'))) {
    return 'pedestrian_in_forklift_zone';
  }
  if (riskType === 'zone_intrusion') return 'person_in_restricted_area';
  return buildReportContextForAlias(riskType) ? riskType : null;
}

export function resolveSafetyControlReviewFromReviewPayload(
  review: SafetyControlReviewPayload | null | undefined,
): SafetyControlReviewViewModel | null {
  return toViewModelFromReview(review);
}

export function resolveSafetyControlReviewFromPolicyConfig(
  config: Record<string, unknown> | null | undefined,
): SafetyControlReviewViewModel | null {
  const raw = asObject(config);
  const directContext = asObject(raw.report_context) as PolicyReportContextPayload;
  if (directContext?.safety_control_review) return toViewModel(directContext);
  const alias = asText(asObject(raw.ui_context).policy_alias);
  return toViewModelFromReview(alias ? buildSafetyControlReviewForAlias(alias) : null);
}

export function resolveSafetyControlReviewFromAlias(
  alias: string | null | undefined,
): SafetyControlReviewViewModel | null {
  const key = asText(alias);
  return toViewModelFromReview(key ? buildSafetyControlReviewForAlias(key) : null);
}

export function resolveSafetyControlReviewFromPolicyContext(
  policyContext: PolicyReportContextPayload | PolicyReportContextBlock | null | undefined,
): SafetyControlReviewViewModel | null {
  return toViewModel(policyContext);
}

export function resolveSafetyControlReviewFromArtifactDetail(
  detail: SafetyReportArtifactDetailResponse | null | undefined,
): SafetyControlReviewViewModel | null {
  const directCandidates = [
    detail?.technical_details?.safety_control_review,
    detail?.safety_scan_buyer_report?.safety_control_review,
    (detail?.safety_scan_buyer_report as Record<string, unknown> | undefined)?.safety_control_scan
      && asObject(
        (detail?.safety_scan_buyer_report as Record<string, unknown>).safety_control_scan,
      ).safety_control_review,
  ];
  for (const candidate of directCandidates) {
    const resolved = toViewModelFromReview(candidate);
    if (resolved) return resolved;
  }
  const policyCandidates = [
    detail?.technical_details?.policy_report_context,
    detail?.technical_details?.report_context,
    detail?.safety_scan_buyer_report?.policy_report_context,
  ];
  for (const candidate of policyCandidates) {
    const resolved = toViewModel(candidate || null);
    if (resolved) return resolved;
  }
  const alias = inferAliasFromRecord({
    riskType: detail?.artifact?.scope?.risk_type,
    activityType: detail?.artifact?.display_labels?.activity_type_label,
    riskLabel: detail?.artifact?.display_labels?.risk_level,
    unsafeConditionTitle: detail?.artifact?.summary,
  });
  return toViewModelFromReview(alias ? buildSafetyControlReviewForAlias(alias) : null);
}

export function resolveSafetyControlReviewFromProofPreview(
  preview: ReliabilityProofPackPreviewResponse | null | undefined,
): SafetyControlReviewViewModel | null {
  const context = asObject(
    preview?.evidence_pack?.report_projection?.label_bundle?.policy_report_context,
  ) as PolicyReportContextPayload;
  const labelBundle = asObject(preview?.evidence_pack?.report_projection?.label_bundle);
  const direct = toViewModelFromReview(labelBundle.safety_control_review);
  if (direct) return direct;
  return toViewModel(context);
}

export function resolveSafetyControlReviewFromSafetyCase(
  item: SafetyCaseRecord | SafetyDashboardRow | null | undefined,
): SafetyControlReviewViewModel | null {
  const scope = asObject((item as SafetyCaseRecord | undefined)?.scope);
  const topLevel = asObject(item);
  const directContext = asObject(
    topLevel.policy_report_context
    || topLevel.report_context
    || scope.policy_report_context
    || scope.report_context,
  ) as PolicyReportContextPayload;
  if (directContext?.safety_control_review) return toViewModel(directContext);
  const alias = inferAliasFromRecord({
    riskType: item?.risk_type,
    activityType: (item as SafetyDashboardRow | undefined)?.activity_type,
    riskLabel: item?.risk_label,
    hazardCategory: item?.hazard_category,
    unsafeConditionTitle: (item as SafetyDashboardRow | undefined)?.unsafe_condition_title,
  });
  return toViewModelFromReview(alias ? buildSafetyControlReviewForAlias(alias) : null);
}

function asBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
}

export function resolveSafetyControlReviewV9Parity(
  detail: SafetyReportArtifactDetailResponse | null | undefined,
): SafetyControlReviewV9ParityViewModel | null {
  const reportV2 = detail?.safety_scan_report_v2
    || asObject(detail?.technical_details).safety_scan_report_v2;
  const reportRecord = asObject(reportV2);
  const sections = asObject(
    reportRecord.customer_ready_sections
    || detail?.safety_scan_buyer_report?.customer_ready_sections,
  );
  const layoutMode = asText(
    reportRecord.customer_layout_mode
    || sections.customer_layout_mode
    || asObject(detail?.technical_details).customer_layout_mode,
  );
  if (!['customer_clean_v9', 'buyer_ready_v3', 'ready_v3'].includes(layoutMode) && !sections.report_mode) {
    return null;
  }
  const source = Object.keys(sections).length ? sections : reportRecord;
  return {
    reportMode: asText(source.report_mode) || layoutMode || 'buyer_ready_v3',
    sectionKeys: asStringList(source.section_keys),
    reviewerDecisionStatus: asText(source.reviewer_decision_status) || V9_PENDING_DECISION_LABEL,
    privacyRedacted: asBoolean(source.privacy_redacted, true),
    fieldVerificationStatus: asText(source.field_verification_status) || V9_PENDING_FIELD_VERIFICATION_LABEL,
    customerOwnedRiskBoundary: asText(source.customer_owned_risk_boundary),
    correctiveActionStatus: asText(source.corrective_action_status) || 'Not applied',
    followUpClaimBoundary: asText(source.follow_up_claim_boundary),
    closureStatus: asText(source.closure_status) || V9_PENDING_CLOSURE_LABEL,
    secureEvidenceReferencePolicy: asText(source.secure_evidence_reference_policy),
  };
}
